import {
	Card,
	CardContent,
	CardHeader,
	CardTitle,
} from "@veil/ui/components/card";
import { useEffect, useRef } from "react";

interface OperationLogViewerProps {
	/**
	 * Full log text as it stands right now. Parent (OperationDetail) owns
	 * the SSE connection + log buffering so the same connection can also
	 * dispatch step events to the timeline component without us opening a
	 * second EventSource.
	 */
	text: string;
	/** Operation status; controls the empty-state copy. */
	status: string;
}

/**
 * Read-only renderer over a log buffer owned by the parent. The previous
 * version of this component owned the SSE connection itself; we lifted
 * that into `OperationDetail` so both this card and the new
 * `OperationStepTimeline` can read from the same live stream without
 * doubling up subscriptions.
 */
export function OperationLogViewer({ text, status }: OperationLogViewerProps) {
	const containerRef = useRef<HTMLPreElement>(null);

	// Auto-scroll on every log mutation. `text.length` participates in the
	// effect body so biome's exhaustive-deps check sees the dependency
	// without us suppressing it — the actual scroll target is the ref.
	useEffect(() => {
		const el = containerRef.current;
		if (!el) return;
		if (text.length === 0) return;
		el.scrollTop = el.scrollHeight;
	}, [text]);

	return (
		<Card>
			<CardHeader>
				<CardTitle className="text-base">Live log</CardTitle>
			</CardHeader>
			<CardContent>
				<pre
					ref={containerRef}
					className="max-h-[500px] overflow-auto rounded-md bg-zinc-950 p-4 font-mono text-xs leading-relaxed text-zinc-100"
				>
					{text ||
						(status === "running" ? "Waiting for output…" : "No log captured.")}
				</pre>
			</CardContent>
		</Card>
	);
}
